import { useEffect, useState } from "react";
import { C, F, btnBase } from "../theme/tokens";

/** Altezza della top bar delle pagine dell'area di lavoro e dell'account. */
export const ALTEZZA_BARRA = 72;

/** Vero sotto i 768px: la barra laterale diventa un pannello a scomparsa. */
export function useMobile() {
  const [mobile, setMobile] = useState(() => (typeof window === "undefined" ? false : window.innerWidth < 768));
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mobile;
}

/** Icona di uscita per la top bar: la stessa nell'area di lavoro e nell'account. */
export function BottoneEsci({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Esci"
      aria-label="Esci"
      style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, width: 36, height: 36, cursor: "pointer", color: C.textTitle, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
      </svg>
    </button>
  );
}

/** Pulsante che apre la barra laterale su telefono. */
export function BottoneMenu({ onClick }) {
  return (
    <button type="button" onClick={onClick} aria-label="Apri il menu" style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, width: 36, height: 36, cursor: "pointer", color: C.textTitle, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
    </button>
  );
}

/**
 * Mattoni delle pagine account: stessa barra in alto della pagina di
 * anonimizzazione, una scheda centrata, campi e pulsanti coerenti con il
 * design system. Tutto a stili inline come il resto del frontend.
 */

const Freccia = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

export function BarraAlta({ titolo, onBack, etichettaBack = "Torna indietro", destra, sinistra }) {
  return (
    <div style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: "0 16px", minHeight: ALTEZZA_BARRA, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, position: "sticky", top: 0, zIndex: 50 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
        {sinistra}
        {onBack && (
          <button type="button" onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", color: C.smartBlue, fontSize: 14, fontWeight: 500, fontFamily: F.b, display: "flex", alignItems: "center", gap: 6, padding: 0 }}>
            <Freccia /> {etichettaBack}
          </button>
        )}
        {onBack && <span style={{ color: C.border }}>|</span>}
        <h1 style={{ fontSize: 17, fontWeight: 500, fontFamily: F.h, color: C.textTitle, margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{titolo}</h1>
      </div>
      {destra && <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>{destra}</div>}
    </div>
  );
}

export function PaginaAccount({ titolo, sottotitolo, onBack, etichettaBack, larghezza = 520, destra, sinistra, lato, children }) {
  // `lato` e' la barra laterale: quando c'e', la pagina e' una colonna
  // accanto a lei, come l'area di lavoro.
  return (
    <div style={{ minHeight: "100vh", background: C.smartBlue10, fontFamily: F.b, display: "flex", alignItems: "stretch" }}>
      {lato}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <BarraAlta titolo={titolo} onBack={onBack} etichettaBack={etichettaBack} destra={destra} sinistra={sinistra} />
        <div style={{ maxWidth: larghezza, width: "100%", boxSizing: "border-box", margin: "0 auto", padding: "32px 16px 64px" }}>
          {sottotitolo && <p style={{ margin: "0 0 20px", fontSize: 15, color: C.textNum, lineHeight: 1.5 }}>{sottotitolo}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

export function Scheda({ titolo, descrizione, children, style = {} }) {
  return (
    <section style={{ background: C.white, borderRadius: C.radiusSection, border: `1px solid ${C.border}`, padding: 24, marginBottom: 20, boxShadow: C.shadow, ...style }}>
      {titolo && <h2 style={{ fontSize: 18, fontWeight: 600, fontFamily: F.h, color: C.textTitle, margin: "0 0 6px" }}>{titolo}</h2>}
      {descrizione && <p style={{ margin: "0 0 16px", fontSize: 13, color: C.textNum, lineHeight: 1.5 }}>{descrizione}</p>}
      {children}
    </section>
  );
}

export function Campo({ etichetta, tipo = "text", valore, onChange, autoComplete, placeholder, richiesto = true, aiuto, minLength, disabilitato }) {
  return (
    <label style={{ display: "block", marginBottom: 14 }}>
      <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.textTitle, marginBottom: 6, letterSpacing: "0.02em" }}>{etichetta}</span>
      <input
        type={tipo}
        value={valore}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        placeholder={placeholder}
        required={richiesto}
        minLength={minLength}
        disabled={disabilitato}
        style={{ width: "100%", boxSizing: "border-box", borderRadius: 10, border: `1px solid ${C.border}`, padding: "11px 12px", fontSize: 14, fontFamily: F.b, color: C.textBody, background: disabilitato ? C.sectionAlt : C.smartWhite, outline: "none" }}
      />
      {aiuto && <span style={{ display: "block", marginTop: 5, fontSize: 12, color: C.textNum }}>{aiuto}</span>}
    </label>
  );
}

const VARIANTI = {
  primario: { background: C.btnBg, color: "#fff", border: "1px solid transparent" },
  blu: { background: C.smartBlue, color: "#fff", border: "1px solid transparent" },
  secondario: { background: C.white, color: C.textTitle, border: `1px solid ${C.border}` },
  pericolo: { background: C.white, color: "#b42318", border: "1px solid #f2c4c0" },
};

export function Bottone({ children, variante = "primario", onClick, tipo = "button", disabilitato = false, pieno = false, piccolo = false, style = {} }) {
  const [hover, setHover] = useState(false);
  const base = VARIANTI[variante] || VARIANTI.primario;
  return (
    <button
      type={tipo}
      onClick={onClick}
      disabled={disabilitato}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...btnBase, ...base,
        height: piccolo ? 34 : 44, padding: piccolo ? "0 14px" : "0 22px",
        fontSize: piccolo ? 12 : 13, width: pieno ? "100%" : undefined,
        cursor: disabilitato ? "not-allowed" : "pointer", opacity: disabilitato ? 0.6 : hover ? 0.9 : 1,
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
        transition: "opacity .15s ease", ...style,
      }}
    >
      {children}
    </button>
  );
}

export function LinkTesto({ onClick, children, style = {} }) {
  return (
    <button type="button" onClick={onClick} style={{ background: "none", border: "none", padding: 0, color: C.smartBlue, fontWeight: 600, cursor: "pointer", fontSize: 13, fontFamily: F.b, textDecoration: "underline", ...style }}>
      {children}
    </button>
  );
}

const AVVISI = {
  info: { background: C.smartBlue10, border: C.border, color: C.textBody },
  ok: { background: "#eefaf2", border: "#c6ecd2", color: "#1f6b3a" },
  errore: { background: "#fff0f0", border: "#f5c6c6", color: "#c0392b" },
  attenzione: { background: "#fff8e6", border: "#f4dfa6", color: "#8a5a00" },
};

export function Avviso({ tipo = "info", children, style = {} }) {
  const s = AVVISI[tipo] || AVVISI.info;
  return (
    <div role={tipo === "errore" ? "alert" : "status"} style={{ padding: "11px 14px", borderRadius: 10, background: s.background, border: `1px solid ${s.border}`, fontSize: 13, color: s.color, lineHeight: 1.5, margin: "0 0 14px", ...style }}>
      {children}
    </div>
  );
}

export function Riga({ children, style = {} }) {
  return <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", ...style }}>{children}</div>;
}

export function dataIt(iso) {
  if (!iso) return "";
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("it-IT");
}
